import { useEffect, useRef, useState } from "react";
import type { BBox, WazeApiResponse } from "@shared/types";
import { bboxKey } from "@/lib/bbox";

const POLL_MS = 20_000;

export function useWazeAlerts(bbox: BBox | null): {
  data: WazeApiResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [data, setData] = useState<WazeApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const versionRef = useRef(0);
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!bbox) return;
    const key = bboxKey(bbox);
    keyRef.current = key;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      const myVersion = ++versionRef.current;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          top: String(bbox.top),
          bottom: String(bbox.bottom),
          left: String(bbox.left),
          right: String(bbox.right),
        });
        const res = await fetch(`/api/alerts?${params.toString()}`, {
          method: "GET",
          headers: { accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as WazeApiResponse;
        if (cancelled || keyRef.current !== key || myVersion !== versionRef.current) return;
        setData(json);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
        timer = setTimeout(tick, POLL_MS);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [bbox]);

  const refetch = () => {
    versionRef.current++;
    keyRef.current = null;
    setData((d) => d);
  };

  return { data, loading, error, refetch };
}
